import type {
    IChangeEmail,
    IOnboardingStateResponse,
    IUserResponse,
    IUpdateUser,
    IUpdateUserProfile,
    IUserTenantResponse,
    IVerificationEmailResponse,
} from '@escloud/types';

import type { ApiClient } from './api-client';

import { getApiUrlForRegion } from '@/lib/region-config';

export class UsersService {
    constructor(private api: ApiClient) {}

    getCurrentUser(): Promise<IUserResponse> {
        return this.api.get<IUserResponse>('/users/me');
    }

    updateCurrentUser(data: IUpdateUser): Promise<IUserResponse> {
        return this.api.patch<IUserResponse>('/users/me', data);
    }

    /**
     * Professional profile / consent update (ESCLOUD-278). Partial: an omitted
     * field is left alone. Requires a verified address and a user row in the
     * targeted region. `region` targets a specific regional API: the funnel
     * has no tenant in context yet, so it names the intent's region explicitly.
     */
    updateProfile(data: IUpdateUserProfile, region?: string): Promise<IUserResponse> {
        return this.api.patch<IUserResponse>('/users/me/profile', data, this.target(region));
    }

    /**
     * Replace the sign-in address while it is still unverified (funnel v2).
     * `region` targets a specific regional API: the funnel has no tenant in
     * context yet, so it names the intent's region explicitly. Refusals: 409
     * `email_already_verified`, 409 `email_in_use` (non-disclosing), 400
     * `email_change_unsupported`, 429 `verification_email_rate_limited` (it
     * spends the resend allowance), 503 `email_change_unavailable`.
     */
    changeEmail(data: IChangeEmail, region?: string): Promise<IUserResponse> {
        return this.api.patch<IUserResponse>('/users/me/email', data, this.target(region));
    }

    /**
     * What this region still wants before the user can work (ESCLOUD-278).
     *
     * The server's own answer to "is the professional profile complete",
     * recomputed from the current required-field list rather than read off the
     * `profileCompletedAt` latch — which records when a profile FIRST became
     * complete and is not evidence that it still is. Any client gate must take
     * completeness from here, so widening the required set re-gates users in
     * one place instead of two apps disagreeing.
     *
     * Works before a user row exists (`userExists: false`), which is how the
     * verify-email screen can use it too.
     */
    getOnboardingState(): Promise<IOnboardingStateResponse> {
        return this.api.get<IOnboardingStateResponse>('/users/me/onboarding-state');
    }

    /**
     * Ask the identity provider to resend the verification email (ESCLOUD-278).
     * Works before any user row exists; 202 means requested, not delivered.
     * Refusals carry `email_already_verified` (400),
     * `verification_email_rate_limited` (429) or
     * `verification_email_unavailable` (503).
     */
    resendVerificationEmail(): Promise<IVerificationEmailResponse> {
        return this.api.post<IVerificationEmailResponse>('/users/me/verification-email');
    }

    /**
     * Memberships as ONE region's API sees them — each regional API only
     * queries its own database. `baseUrl` targets a specific region; without it
     * the request follows the region of the tenant currently in context.
     */
    getUserTenants(baseUrl?: string): Promise<{ items: IUserTenantResponse[] }> {
        return this.api.get<{ items: IUserTenantResponse[] }>('/users/me/tenants', { baseUrl });
    }

    private target(region: string | undefined): { baseUrl?: string } | undefined {
        return region ? { baseUrl: getApiUrlForRegion(region) } : undefined;
    }
}
